import { useEffect, useState } from "react";
import { Landmark, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";

const KINDS = [
    { key: "finanzierung", title: "Favorisierte Bank(en) für Finanzierung" },
    { key: "leasing", title: "Favorisierte Leasinggesellschaft(en)" },
];

/**
 * Aug-2026 (User-Wunsch) — Zahnrad: favorisierte Bank(en) für Finanzierung und
 * Leasinggesellschaft(en) hinterlegen (Daten aus Partnerunternehmen). Sind
 * Favoriten hinterlegt, zeigt der Verkaufsprozess NUR noch diese an — je
 * Partner optional nach Fahrzeug-Herstellern eingeschränkt (leer = alle).
 */
export function FinancePartnerFavorites() {
    const [banks, setBanks] = useState(null);
    const [favs, setFavs] = useState({ finanzierung: [], leasing: [] });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        Promise.all([
            api.get("/dealer/banks").catch(() => ({ data: { banks: [] } })),
            api.get("/dealer/finance-partner-settings").catch(() => ({ data: {} })),
        ]).then(([b, f]) => {
            setBanks(b.data?.banks || []);
            setFavs(f.data?.favorites || { finanzierung: [], leasing: [] });
        });
    }, []);

    const save = async (next) => {
        setFavs(next);
        setSaving(true);
        try {
            await api.patch("/dealer/finance-partner-settings", { favorites: next });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally { setSaving(false); }
    };

    const toggle = (kind, bank) => {
        const list = favs[kind] || [];
        const next = list.some((f) => f.bank_id === bank.id)
            ? list.filter((f) => f.bank_id !== bank.id)
            : [...list, { bank_id: bank.id, name: bank.name, brands: [] }];
        save({ ...favs, [kind]: next });
    };

    const setBrandsText = (kind, bankId, text) => {
        setFavs((prev) => ({
            ...prev,
            [kind]: (prev[kind] || []).map((f) => (f.bank_id === bankId ? { ...f, _brandsText: text } : f)),
        }));
    };

    const commitBrands = (kind, bankId) => {
        const next = {
            ...favs,
            [kind]: (favs[kind] || []).map((f) => {
                if (f.bank_id !== bankId) return f;
                const src = f._brandsText !== undefined ? f._brandsText : (f.brands || []).join(", ");
                const { _brandsText, ...rest } = f;
                return { ...rest, brands: src.split(",").map((s) => s.trim()).filter(Boolean) };
            }),
        };
        save(next);
    };

    if (!banks) return null;
    return (
        <div className="border-t border-gray-200" data-testid="finance-partner-favorites">
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                <Landmark className="w-3.5 h-3.5 text-gray-500" />
                <span className="text-[11px] font-bold uppercase tracking-wider text-gray-700">Finanzierungspartner (Favoriten)</span>
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
            </div>
            <p className="text-[10px] text-gray-500 px-3 pt-2">
                Daten aus <b>Partnerunternehmen</b>. Sind Favoriten hinterlegt, werden im Verkaufsprozess <b>nur noch diese</b> angezeigt — passend zum Fahrzeug-Hersteller (Feld leer = alle Hersteller).
            </p>
            {KINDS.map(({ key, title }) => {
                const list = banks.filter((b) => !(b.finance_types || []).length || (b.finance_types || []).includes(key));
                return (
                    <div key={key} className="px-3 py-2" data-testid={`finance-favorites-${key}`}>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">{title}</div>
                        {list.length === 0 ? (
                            <div className="text-[11px] text-gray-400">Keine passenden Partner unter „Partnerunternehmen" hinterlegt.</div>
                        ) : (
                            <div className="space-y-1">
                                {list.map((b) => {
                                    const fav = (favs[key] || []).find((f) => f.bank_id === b.id);
                                    return (
                                        <div key={b.id} className="flex items-center gap-2 flex-wrap" data-testid={`finance-favorite-row-${key}-${b.id}`}>
                                            <label className="flex items-center gap-1.5 cursor-pointer text-[12px] text-gray-800 min-w-[180px]">
                                                <input
                                                    type="checkbox"
                                                    checked={Boolean(fav)}
                                                    onChange={() => toggle(key, b)}
                                                    className="w-3.5 h-3.5 accent-emerald-600"
                                                    data-testid={`finance-favorite-check-${key}-${b.id}`}
                                                />
                                                <span className={fav ? "font-bold" : ""}>{b.name}{b.city ? ` (${b.city})` : ""}</span>
                                            </label>
                                            {fav && (
                                                <input
                                                    type="text"
                                                    value={fav._brandsText !== undefined ? fav._brandsText : (fav.brands || []).join(", ")}
                                                    onChange={(e) => setBrandsText(key, b.id, e.target.value)}
                                                    onBlur={() => commitBrands(key, b.id)}
                                                    placeholder="Hersteller, Komma-getrennt (leer = alle) — z. B. Vespa, Piaggio, Suzuki"
                                                    className="flex-1 min-w-[220px] h-7 text-[11px] border border-gray-300 rounded-sm px-2"
                                                    data-testid={`finance-favorite-brands-${key}-${b.id}`}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
